// ScionCarbon API client.
//
// Single place where the SPA talks to the Laravel API. Before this existed the
// frontend made zero network calls and `signIn()` simply set a boolean — see
// docs/audit/01-auth-and-access.md.
//
// Base URL resolution, in order:
//   1. window.SCIONCARBON_API_BASE_URL  (set it in index.html for a deployment)
//   2. same-origin /api                 (when the API is reverse-proxied alongside the SPA)
//   3. http://127.0.0.1:8099/api        (the docker dev stack: npm run api:up)
//
// NOTE: the token lives in localStorage. That is readable by any script on the
// origin, so it is only acceptable while the strict CSP (script-src 'self') holds
// and no third-party scripts are added. An httpOnly cookie is the stronger
// option and needs a backend change — tracked as roadmap R11.

const DEV_API = "http://127.0.0.1:8099/api";
const TOKEN_KEY = "scioncarbon.token";
const USER_KEY = "scioncarbon.user";

function resolveBaseUrl() {
  if (typeof window !== "undefined" && window.SCIONCARBON_API_BASE_URL) {
    return String(window.SCIONCARBON_API_BASE_URL).replace(/\/$/, "");
  }
  if (import.meta?.env?.VITE_API_BASE_URL) {
    return String(import.meta.env.VITE_API_BASE_URL).replace(/\/$/, "");
  }
  if (typeof location !== "undefined" && /^(8097|5173|4173)$/.test(location.port) === false && location.protocol.startsWith("http")) {
    // Served from the same origin as the API (production/nginx): use a relative path.
    return "/api";
  }
  return DEV_API;
}

export const apiBaseUrl = resolveBaseUrl();

/** Thrown for any non-2xx response. `status` lets callers distinguish 401 from 422. */
export class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export const session = {
  get token() {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  },
  get user() {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
  },
  save(token, user) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch { /* private mode — session simply won't survive reload */ }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch { /* ignore */ }
  }
};

/** Called when the API rejects our token, so the app can drop to the public site. */
let onUnauthorized = () => {};
export function setUnauthorizedHandler(fn) {
  onUnauthorized = typeof fn === "function" ? fn : () => {};
}

async function request(path, { method = "GET", body, auth = true, formData } = {}) {
  const headers = { Accept: "application/json" };
  // FormData sets its own multipart boundary — never set Content-Type for it.
  if (body !== undefined && !formData) headers["Content-Type"] = "application/json";
  if (auth && session.token) headers.Authorization = `Bearer ${session.token}`;

  let response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method,
      headers,
      body: formData ? body : (body === undefined ? undefined : JSON.stringify(body))
    });
  } catch (networkError) {
    // Offline, DNS failure, API container down, or CSP connect-src refusal.
    throw new ApiError(
      "Cannot reach the ScionCarbon server. Check your connection and try again.",
      0,
      { cause: String(networkError) }
    );
  }

  const raw = await response.text();
  let payload = null;
  if (raw) {
    try { payload = JSON.parse(raw); } catch { payload = { message: raw }; }
  }

  if (response.status === 401 && auth && session.token) {
    // Our stored token is no longer good — expired, revoked, or the account was
    // unapproved. Drop it and let the app return to the public site.
    session.clear();
    onUnauthorized();
  }

  if (!response.ok) {
    throw new ApiError(errorMessageFor(response.status, payload), response.status, payload);
  }

  return payload;
}

function errorMessageFor(status, payload) {
  if (payload) {
    if (payload.error) return payload.error;
    if (payload.message) return payload.message;
  }
  if (status === 401) return "Email or password is incorrect.";
  if (status === 403) return "Your role does not permit that action.";
  if (status === 404) return "That record does not exist, or you do not have access to it.";
  if (status === 429) return "Too many attempts. Please wait a moment and try again.";
  if (status >= 500) return "The ScionCarbon server had a problem. Please try again.";
  return "Something went wrong. Please try again.";
}

/**
 * Map an API project record onto the shape the UI renders.
 * The API is snake_case and stores money/volume as decimal strings; the view
 * layer expects camelCase and numbers.
 */
export function toUiProject(record) {
  return {
    id: record.id,
    name: record.name,
    owner: record.owner,
    ownerType: record.owner_type,
    organizationId: record.organization_id,
    category: record.category,
    location: record.location,
    stage: record.stage,
    status: record.status,
    risk: record.risk,
    score: record.score,
    methodology: record.methodology,
    reviewer: record.reviewer,
    officer: record.officer,
    tco2e: Number(record.tco2e) || 0,
    value: Number(record.commercial_value) || 0,
    docs: Number(record.docs_percent) || 0,
    mrv: Number(record.mrv_percent) || 0,
    marketability: record.marketability,
    coordinates: record.coordinates,
    area: record.area,
    nextAction: record.next_action,
    summary: record.summary,
    commercialModel: record.commercial_model,
    benefitShare: record.benefit_share,
    endorsementStatus: record.endorsement_status,
    visibleTo: Array.isArray(record.visible_to) ? record.visible_to : [],
    statusHistory: Array.isArray(record.status_history) ? record.status_history : [],
    ndaWithheld: Boolean(record.nda_withheld),
    metadata: record.metadata || {}
  };
}

/** Map an API message thread onto the shape the mailbox renders. */
export function toUiThread(thread, roleLabels = {}) {
  const messages = Array.isArray(thread.messages) ? thread.messages : [];
  return {
    id: thread.id,
    projectId: thread.project_id,
    feasibilityStudyId: thread.feasibility_study_id || null,
    subject: thread.subject,
    status: thread.status || "open",
    priority: thread.status === "urgent" ? "High" : "Normal",
    participants: Array.isArray(thread.participants) ? thread.participants : [],
    sciontechNotified: true,
    owner: thread.owner || "Project owner",
    buyer: thread.buyer || "Counterparty",
    messages: messages.map((message) => ({
      id: message.id,
      from: message.actor_role || "system",
      // actorId and acknowledgedBy drive unread state: a message is unread for
      // you if you did not write it and have not acknowledged it.
      actorId: message.actor_id != null ? String(message.actor_id) : null,
      acknowledgedBy: Array.isArray(message.acknowledged_by) ? message.acknowledged_by.map(String) : [],
      name: roleLabels[message.actor_role] || message.actor_role || "ScionCarbon",
      time: String(message.created_at || "").replace("T", " ").slice(0, 16),
      text: message.body || "",
      audience: "Project owner and permitted reviewers"
    }))
  };
}

/** Map an API feasibility study onto the shape the reviewer queue renders. */
export function toUiStudy(study) {
  const evidence = study.evidence && typeof study.evidence === "object" ? study.evidence : {};
  return {
    id: study.id,
    projectId: study.project_id,
    project: study.title || study.project_id,
    owner: study.owner || study.organization_id || "Project owner",
    type: study.project_type || "Carbon project",
    status: study.status || "submitted",
    score: study.readiness_score != null ? `${study.readiness_score}%` : "—",
    reviewer: study.reviewer || "Sciontech Processor",
    records: evidence.records || evidence.mrv || "Submitted evidence",
    media: evidence.media || "See project data room",
    next: study.return_comment || (study.auto_evaluation && study.auto_evaluation.decision) || "Awaiting reviewer action"
  };
}

export const api = {
  health: () => request("/health", { auth: false }),

  /**
   * Sign in.
   *
   * Returns either a completed session or an MFA challenge. A correct password
   * does NOT produce a token when a second factor is due — the server issues a
   * challenge that can do nothing except complete or enrol.
   */
  async login(email, password, remember = true) {
    const data = await request("/auth/login", {
      method: "POST",
      auth: false,
      body: { email, password, remember }
    });

    if (data?.mfa_required) {
      return { mfaRequired: true, purpose: data.purpose, challenge: data.challenge, message: data.message };
    }

    if (!data?.token || !data?.user) {
      throw new ApiError("The server did not return a valid session.", 0, data);
    }
    session.save(data.token, data.user);
    return { mfaRequired: false, user: data.user };
  },

  /** Complete a sign-in challenge with a TOTP or recovery code. */
  async verifyMfa(challenge, code) {
    const data = await request("/auth/mfa/verify", {
      method: "POST", auth: false, body: { challenge, code }
    });
    if (!data?.token || !data?.user) throw new ApiError("The server did not return a valid session.", 0, data);
    session.save(data.token, data.user);
    return data;
  },

  /** Start enrolment — with a challenge (role-mandated) or a session (voluntary). */
  beginMfaEnrolment: (challenge) =>
    request("/auth/mfa/enroll", { method: "POST", auth: !challenge, body: challenge ? { challenge } : {} }),

  /** Finish enrolment. Returns recovery codes once, plus a session if mid-sign-in. */
  async confirmMfaEnrolment(code, challenge) {
    const data = await request("/auth/mfa/confirm", {
      method: "POST", auth: !challenge, body: challenge ? { challenge, code } : { code }
    });
    if (data?.token && data?.user) session.save(data.token, data.user);
    return data;
  },

  disableMfa: (password, code) =>
    request("/auth/mfa/disable", { method: "POST", body: { password, code } }),

  /** Resolve the signed-in user from the token, server-side. */
  async me() {
    const data = await request("/auth/me");
    if (!data?.user) throw new ApiError("The server did not identify the session.", 0, data);
    return { user: data.user, app: data.app || null };
  },

  /** Begin a password reset. Answers the same way whether or not the address exists. */
  requestPasswordReset: (email) =>
    request("/auth/forgot-password", { method: "POST", auth: false, body: { email } }),

  /** Complete a password reset. The server revokes every existing session on success. */
  resetPassword: ({ email, token, password, passwordConfirmation }) =>
    request("/auth/reset-password", {
      method: "POST",
      auth: false,
      body: { email, token, password, password_confirmation: passwordConfirmation }
    }),

  async logout() {
    // Best-effort: always clear locally even if the server call fails, so the
    // user is never stuck in a session they asked to end.
    try { await request("/auth/logout", { method: "POST" }); } catch { /* ignore */ }
    session.clear();
  },

  projects: ({ page = 1, q = "" } = {}) => {
    const params = new URLSearchParams({ page: String(page) });
    if (q) params.set("q", q);
    return request(`/projects?${params}`);
  },
  project: (id) => request(`/projects/${encodeURIComponent(id)}`),
  feasibilityStudies: ({ page = 1 } = {}) => request(`/feasibility-studies?page=${page}`),

  /** Submit a feasibility study. */
  createFeasibilityStudy: (fields) =>
    request("/feasibility-studies", { method: "POST", body: fields }),

  feasibilityStudy: (id) => request(`/feasibility-studies/${encodeURIComponent(id)}`),

  /**
   * Run the screening evaluation over a study.
   *
   * Returns a readiness score, an indicative volume and the gaps that stand
   * between the study and endorsement.
   */
  evaluateFeasibilityStudy: (id, fields) =>
    request(`/feasibility-studies/${encodeURIComponent(id)}/evaluate`, { method: "POST", body: fields }),

  /** The screening calculators, and the runs saved against a project. */
  calculatorTemplates: () => request("/calculator-templates"),

  calculationRuns: (projectId) =>
    request(`/projects/${encodeURIComponent(projectId)}/calculation-runs`),

  saveCalculationRun: (projectId, fields) =>
    request(`/projects/${encodeURIComponent(projectId)}/calculation-runs`, { method: "POST", body: fields }),

  // --- MRV Engine (M7) ------------------------------------------------------

  /** Monitoring records for a project, over time. */
  mrvRecords: (projectId) =>
    request(`/projects/${encodeURIComponent(projectId)}/mrv-records`),

  /** Record a monitoring measurement. Owner or reviewer only, server-enforced. */
  saveMrvRecord: (projectId, fields) =>
    request(`/projects/${encodeURIComponent(projectId)}/mrv-records`, { method: "POST", body: fields }),
  messages: ({ page = 1 } = {}) => request(`/messages?page=${page}`),

  /** Post a reply into a project's thread. */
  postProjectMessage: (projectId, body) =>
    request(`/projects/${encodeURIComponent(projectId)}/messages`, { method: "POST", body: { body } }),

  /** Read the thread for one project. */
  projectMessages: (projectId) =>
    request(`/projects/${encodeURIComponent(projectId)}/messages`),

  /** Mark a message read. Scoped server-side to threads you participate in. */
  acknowledgeMessage: (messageId) =>
    request(`/messages/${encodeURIComponent(messageId)}/acknowledge`, { method: "POST" }),
  auditLogs: ({ page = 1 } = {}) => request(`/audit-logs?page=${page}`),

  /** What has happened that concerns you. Scoped to you, server-side. */
  notifications: () => request("/notifications"),
  readNotification: (id) => request(`/notifications/${encodeURIComponent(id)}/read`, { method: "POST" }),
  readAllNotifications: () => request("/notifications/read-all", { method: "POST" }),

  // --- administration (J5) --------------------------------------------------

  /** Every account. Administrator and Sciontech only, enforced server-side. */
  adminUsers: () => request("/admin/users"),

  /**
   * Assign a role.
   *
   * The server refuses an unknown role, refuses removing the last
   * administrator, and revokes the target's sessions so the change takes
   * effect everywhere rather than at their next sign-in.
   */
  setUserRole: (userId, role) =>
    request(`/admin/users/${encodeURIComponent(userId)}/role`, { method: "PATCH", body: { role } }),

  /**
   * Runtime settings (M13). Administrator only, enforced server-side.
   *
   * adminSettings returns the settings grouped, each with its current value,
   * shipped default and bounds. saveSettings sends a { key: value } map; a null
   * value resets that key to its default. Validation is all-or-nothing: a 422
   * carries per-key messages and nothing is written.
   */
  adminSettings: () => request("/admin/settings"),

  saveSettings: (settings) =>
    request("/admin/settings", { method: "PATCH", body: { settings } }),

  /**
   * Reports. Each is scoped by the caller's role server-side.
   *
   * Note that feasibility-summary and admin-approvals return exactly what
   * /feasibility-studies and /admin/approval-requests return — they are views
   * over the same data, not separate analyses, and the UI says so.
   */
  reportProjectReadiness: () => request("/reports/project-readiness"),
  reportBuyerBrief: () => request("/reports/buyer-brief"),
  reportFeasibilitySummary: () => request("/reports/feasibility-summary"),
  reportAdminApprovals: () => request("/reports/admin-approvals"),

  // --- writes -------------------------------------------------------------

  createProject: (fields) => request("/projects", { method: "POST", body: fields }),

  /** Change a project's status, with an optional comment for the history. */
  setProjectStatus: (id, status, comment) =>
    request(`/projects/${encodeURIComponent(id)}/status`, {
      method: "POST", body: { status, comment: comment || null }
    }),

  updateProject: (id, fields) =>
    request(`/projects/${encodeURIComponent(id)}`, { method: "PATCH", body: fields }),

  /** Sciontech only. Makes the project visible to Sigma Carbon and buyers. */
  endorseProject: (id) =>
    request(`/projects/${encodeURIComponent(id)}/endorse`, { method: "POST" }),

  /** Sciontech only. `comment` is required by the API. */
  returnProject: (id, comment) =>
    request(`/projects/${encodeURIComponent(id)}/return`, { method: "POST", body: { comment } }),

  // --- counterparty actions (J1) ------------------------------------------
  //
  // Every one of these is role-gated server-side and scoped to records the
  // caller may already see, so a fabricated control in the client changes
  // nothing. `details` is stored against the project and written to the audit
  // log, which is why the UI asks for a note rather than recording a bare click.

  /** Buyer accepts the NDA for a record, unlocking its commercial figures. */
  acceptNda: (projectId) =>
    request(`/projects/${encodeURIComponent(projectId)}/nda`, { method: "POST" }),

  /** Buyer registers interest in an endorsed record. */
  registerBuyerInterest: (projectId, note) =>
    request(`/projects/${encodeURIComponent(projectId)}/buyer-interest`, {
      method: "POST", body: { note: note || null }
    }),

  /**
   * Ask the owner for more information.
   *
   * This posts into the project's message thread, so the question reaches a
   * person rather than becoming another silent flag on a record.
   */
  requestProjectInformation: (projectId, body) =>
    request(`/projects/${encodeURIComponent(projectId)}/buyer-information-request`, {
      method: "POST", body: { body }
    }),

  /** Ask to meet about a record. Open to buyers, Sigma, owners and Sciontech. */
  requestMeeting: (projectId, note) =>
    request(`/projects/${encodeURIComponent(projectId)}/meeting-request`, {
      method: "POST", body: { note: note || null }
    }),

  /** Sigma Carbon records a methodology/marketability review. */
  recordSigmaReview: (projectId, note) =>
    request(`/projects/${encodeURIComponent(projectId)}/sigma-review`, {
      method: "POST", body: { note }
    }),

  /** Sigma Carbon signals it would take this to market. */
  registerMarketInterest: (projectId, note) =>
    request(`/projects/${encodeURIComponent(projectId)}/sigma-market-interest`, {
      method: "POST", body: { note: note || null }
    }),

  endorseFeasibilityStudy: (id) =>
    request(`/feasibility-studies/${encodeURIComponent(id)}/endorse`, { method: "POST" }),

  returnFeasibilityStudy: (id, comment) =>
    request(`/feasibility-studies/${encodeURIComponent(id)}/return`, { method: "POST", body: { comment } }),

  /**
   * Submit an account request. Creates a pending approval an administrator
   * must decide — the account is NOT usable until then.
   */
  registerUser: (fields) => request("/users/register", { method: "POST", auth: false, body: fields }),

  // --- administrator ------------------------------------------------------

  // --- evidence -----------------------------------------------------------

  /** kind: "documents" | "media" | "gis-files" */
  evidence: (projectId, kind = "documents") =>
    request(`/projects/${encodeURIComponent(projectId)}/${kind}`),

  uploadEvidence(projectId, kind, file, fields = {}) {
    const form = new FormData();
    form.append("file", file);
    form.append("title", fields.title || file.name);
    form.append("type", fields.type || kind);
    if (fields.source) form.append("source", fields.source);
    return request(`/projects/${encodeURIComponent(projectId)}/${kind}`, {
      method: "POST",
      body: form,
      formData: true
    });
  },

  evidenceDownloadUrl: (projectId, kind, recordId) =>
    `${apiBaseUrl}/projects/${encodeURIComponent(projectId)}/evidence/${kind}/${encodeURIComponent(recordId)}/download`,

  approvalRequests: () => request("/admin/approval-requests"),

  approveRequest: (id) =>
    request(`/admin/approval-requests/${encodeURIComponent(id)}/approve`, { method: "POST" }),

  /** `reason` is required by the API. */
  denyRequest: (id, reason) =>
    request(`/admin/approval-requests/${encodeURIComponent(id)}/deny`, { method: "POST", body: { reason } })
};

export default api;
